import type { PricingTier } from '@estac1/shared';
import { Button, Card, CardContent, Input, Label, MoneyInput } from '@estac1/ui';
import { Plus, Trash2 } from 'lucide-react';
import type { TierForm } from './types.js';

interface PricingTiersCardProps {
  tiers: TierForm[];
  onUpdateTier: (idx: number, patch: Partial<PricingTier>) => void;
  onAddTier: () => void;
  onRemoveTier: (idx: number) => void;
}

export const PricingTiersCard = ({
  tiers,
  onUpdateTier,
  onAddTier,
  onRemoveTier,
}: PricingTiersCardProps) => (
  <Card>
    <CardContent className="space-y-3 pt-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <h3 className="text-sm font-semibold">Faixas de mensalidade</h3>
        <Button size="sm" variant="outline" className="w-full sm:w-auto" onClick={onAddTier}>
          <Plus className="h-4 w-4" /> Nova faixa
        </Button>
      </div>
      <p className="text-xs text-muted-fg">
        Cada faixa tem uma mensalidade fixa pelo total de vagas declarado. Faixas precisam ser
        contíguas e a última deve terminar exatamente em "Limite máximo de vagas".
      </p>
      <ul className="space-y-2">
        {tiers.map((tier, idx) => (
          <li
            key={tier._id}
            className="grid grid-cols-1 items-end gap-3 rounded border border-border bg-surface p-3 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_auto]"
          >
            <div>
              <Label htmlFor={`tier-${tier._id}-min`} className="text-xs">
                De (vaga)
              </Label>
              <Input
                id={`tier-${tier._id}-min`}
                type="number"
                min={1}
                value={tier.minVacancies}
                onChange={(e) =>
                  onUpdateTier(idx, { minVacancies: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor={`tier-${tier._id}-max`} className="text-xs">
                Até (vaga)
              </Label>
              <Input
                id={`tier-${tier._id}-max`}
                type="number"
                min={1}
                value={tier.maxVacancies}
                onChange={(e) =>
                  onUpdateTier(idx, { maxVacancies: Number.parseInt(e.target.value, 10) || 0 })
                }
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor={`tier-${tier._id}-monthly`} className="text-xs">
                Mensalidade da faixa
              </Label>
              <MoneyInput
                id={`tier-${tier._id}-monthly`}
                value={tier.monthlyCents}
                onChange={(v) => onUpdateTier(idx, { monthlyCents: v })}
                className="mt-1"
              />
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onRemoveTier(idx)}
              disabled={tiers.length === 1}
              aria-label="Remover tier"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);
